"use client";
import clsx from "clsx";
import {
  BarChart3,
  Bell,
  ChevronDown,
  Clock,
  CreditCard,
  File,
  FileCheck,
  Film,
  Image,
  ImageIcon,
  Languages,
  LayoutDashboard,
  Link as LinkIcon,
  MessageCircle,
  Paintbrush,
  Radio,
  Receipt,
  Tv,
  UserCheck,
  UserCog,
  Users,
  Video,
  Cloud,
} from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import useStore from "../../utils/store";

const Sidebar = ({ onClose }) => {
  const permissions = useStore((state) => state.user);

  // Close sidebar when a link is clicked on mobile
  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      onClose?.();
    }
  };

  const menuItems = [
    {
      title: "Main",
      links: [
        {
          title: "Dashboard",
          link: "/",
          icon: LayoutDashboard,
          permission: "getDashboard",
        },
        {
          title: "Analytics",
          icon: BarChart3,
          permission: "getDashboard",
          subMenu: [
            { title: "Executive Dashboard", link: "/analytics/executive" },
            { title: "Content Analytics", link: "/analytics/content" },
            { title: "User Analytics", link: "/analytics/users" },
            { title: "Subscription Analytics", link: "/analytics/subscriptions" },
            { title: "Payment Analytics", link: "/analytics/payments" },
            { title: "Device Analytics", link: "/analytics/devices" },
            { title: "Search Analytics", link: "/analytics/search" },
            { title: "Live Streaming", link: "/analytics/live-streaming" },
            { title: "Ad Analytics", link: "/analytics/ads" },
            { title: "Error & Security", link: "/analytics/errors-security" },
            { title: "Operational", link: "/analytics/operational" },
          ],
        },
      ],
    },
    {
      title: "Content",
      links: [
        {
          title: "Banner",
          link: "/banners",
          icon: Image,
          permission: "getAllBanners",
        },
        {
          title: "Genres",
          link: "/genres",
          icon: Film,
          permission: "getAllGenres",
        },
        {
          title: "Coming Soon",
          link: "/coming-soon",
          icon: Clock,
          permission: "getAllComingSoon",
        },
        {
          title: "Poster",
          link: "/poster",
          icon: ImageIcon,
          permission: "getAllPosters",
        },
        {
          title: "Language",
          link: "/language",
          icon: Languages,
          permission: "getAllLanguages",
        },
      ],
    },
    {
      title: "Partner",
      links: [
        {
          title: "All Partner",
          link: "/partners",
          icon: Paintbrush,
          permission: "getAllCreators",
        },
        {
          title: "Verified Partner",
          link: "/partners/verified",
          icon: UserCheck,
          permission: "getVerifiedCreators",
        },
        {
          title: "Non-Verified Partner",
          link: "/partners/non-verified",
          icon: Users,
          permission: "getNonVerifiedCreators",
        },
      ],
    },
    {
      title: "Series",
      links: [
        {
          title: "Series",
          link: "/series",
          icon: Tv,
          permission: "getAllSeries",
        },
        {
          title: "Pending Series",
          link: "/series/pending",
          icon: Tv,
          permission: "getPendingSeries",
        },
        {
          title: "Rejected Series",
          link: "/series/rejected",
          icon: Tv,
          permission: "getRejectedSeries",
        },
      ],
    },
    {
      title: "Short Drama",
      links: [
        {
          title: "Short Drama",
          link: "/vertical-series",
          icon: Video,
        },
      ],
    },
    {
      title: "Live Channels",
      links: [
        {
          title: "Live Channels",
          link: "/live-channels",
          icon: Radio,
        },
      ],
    },
    {
      title: "Movies",
      links: [
        {
          title: "Movies",
          link: "/movies",
          icon: Tv,
          permission: "getAllMovies",
        },
        {
          title: "Pending Movies",
          link: "/movies/pending",
          icon: Tv,
          permission: "getPendingMovies",
        },
        {
          title: "Rejected Movies",
          link: "/movies/rejected",
          icon: Tv,
          permission: "getRejectedMovies",
        },
      ],
    },
    {
      title: "Documentaries",
      links: [
        {
          title: "Documentaries",
          link: "/documentaries",
          icon: Tv,
          permission: "getAllMovies",
        },
        {
          title: "Pending Documentaries",
          link: "/documentaries/pending",
          icon: Tv,
          permission: "getPendingMovies",
        },
        {
          title: "Rejected Documentaries",
          link: "/documentaries/rejected",
          icon: Tv,
          permission: "getRejectedMovies",
        },
      ],
    },
    {
      title: "Music Videos",
      links: [
        {
          title: "Music Videos",
          link: "/music-videos",
          icon: Tv,
          permission: "getAllMovies",
        },
        {
          title: "Pending Music Videos",
          link: "/music-videos/pending",
          icon: Tv,
          permission: "getPendingMovies",
        },
        {
          title: "Rejected Music Videos",
          link: "/music-videos/rejected",
          icon: Tv,
          permission: "getRejectedMovies",
        },
      ],
    },
    {
      title: "Short Films",
      links: [
        {
          title: "Short Films",
          link: "/short-films",
          icon: Tv,
          permission: "getAllMovies",
        },
        {
          title: "Pending Short Films",
          link: "/short-films/pending",
          icon: Tv,
          permission: "getPendingMovies",
        },
        {
          title: "Rejected Short Films",
          link: "/short-films/rejected",
          icon: Tv,
          permission: "getRejectedMovies",
        },
      ],
    },
    {
      title: "Users",
      links: [
        {
          title: "Enabled Users",
          link: "/users",
          icon: Users,
          permission: "getAllUsers",
        },
        {
          title: "Disabled Users",
          link: "/users/disabled",
          icon: Users,
          permission: "getAllDisableUsers",
        },
      ],
    },
    {
      title: "Subscription",
      links: [
        {
          title: "Active Subscription",
          link: "/active-subscription",
          icon: UserCheck,
          permission: "activeSubscriptionUser",
        },
        {
          title: "Non-Active Subscriptions",
          link: "/admin/non-active-subscriptions",
          icon: UserCheck,
          permission: "getAllSubscriptions",
        },
      ],
    },
    {
      title: "Other",
      links: [
        {
          title: "Upload Link",
          link: "/upload-link",
          icon: LinkIcon,
          permission: "getBulkUpload",
        },
        {
          title: "Bulk Upload",
          link: "/bunny",
          icon: Cloud,
          // keep visible even without permissions; it's an admin tool
        },
        {
          title: "Notification",
          link: "/notification",
          icon: Bell,
          permission: "userNotifications",
        },
        {
          title: "WhatsApp Notification",
          link: "/whatsapp-notification",
          icon: MessageCircle,
          permission: "userNotifications",
        },
        {
          title: "Admin",
          link: "/admin",
          icon: UserCog,
          permission: "getAdmin",
        },
      ],
    },
  ];

  // Filter menu items based on permissions
  const filteredMenuItems = menuItems
    .map((section) => ({
      ...section,
      links: section.links.filter((link) => {
        // Always show Dashboard
        if (link.title === "Dashboard") return true;
        // If no permission is specified, show the item
        if (!link.permission) return true;
        // Check if user has the required permission
        return permissions?.[link.permission] === true;
      }),
    }))
    .filter((section) => section.links.length > 0); // Remove sections with no visible links

  return (
    <aside className="sidebar-content w-full py-5 px-3 flex flex-col gap-0.5 h-full">
      {filteredMenuItems.map((section, index) => (
        <Nav key={index} link={section} index={0} onLinkClick={handleLinkClick} />
      ))}
    </aside>
  );
};

export default Sidebar;

const Nav = ({ link, index, onLinkClick }) => {
  const location = useLocation();
  const hasActiveSub = link?.subMenu?.some((s) => location.pathname === s.link);
  const [open, setOpen] = React.useState(!!hasActiveSub);
  React.useEffect(() => {
    if (hasActiveSub) setOpen(true);
  }, [hasActiveSub]);
  const isActive = link?.link && location.pathname === link.link;

  const linkBaseClass =
    "flex gap-3 font-medium text-sm px-3 py-2.5 rounded-xl items-center transition-all duration-200";
  const linkInactiveClass = "text-zinc-400 hover:bg-zinc-800/80 hover:text-zinc-100";
  const linkActiveClass =
    "bg-blue-600/90 text-white shadow-lg shadow-blue-500/20 border border-blue-500/30 hover:bg-blue-600 hover:text-white";
  const iconInactiveClass = "text-zinc-500 shrink-0";
  const iconActiveClass = "text-white/90 shrink-0";

  // Handle submenu items (e.g. Analytics)
  if (link?.subMenu) {
    return (
      <div key={link.title} className="sidebar-group">
        <button
          onClick={() => setOpen(!open)}
          className={clsx(
            "flex gap-3 rounded-xl text-zinc-400 items-center justify-between px-3 py-2.5 w-full hover:bg-zinc-800/80 hover:text-zinc-100 transition-all duration-200",
            hasActiveSub && "text-zinc-100"
          )}
        >
          <div className="flex gap-3 font-medium text-sm items-center">
            <link.icon size={18} className={clsx("shrink-0", hasActiveSub ? "text-blue-400/80" : "text-zinc-500")} />
            {link.title}
          </div>
          <ChevronDown size={14} className={clsx("shrink-0 opacity-70 transition-transform duration-200", open && "rotate-180")} />
        </button>
        <div
          className={clsx(
            "sidebar-submenu overflow-hidden transition-all duration-200",
            open ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="ml-4 mt-1 mb-2 pl-3 border-l-2 border-zinc-700/60 space-y-0.5">
            {link.subMenu.map((subItem, i) => {
              const subActive = location.pathname === subItem.link;
              return (
                <Link
                  key={i}
                  to={subItem.link}
                  onClick={onLinkClick}
                  className={clsx(
                    "flex gap-2 font-medium text-sm px-3 py-2 rounded-lg items-center transition-all duration-200",
                    subActive ? linkActiveClass : linkInactiveClass
                  )}
                >
                  {subItem.title}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (link?.links) {
    return (
      <div className="flex flex-col gap-0.5">
        {index === 0 ? (
          <div className="sidebar-section">
            <p className="sidebar-section-title font-semibold uppercase text-[11px] text-zinc-500 tracking-widest px-3 py-2.5">
              {link?.title}
            </p>
            <div className="space-y-0.5">
              {link?.links?.map((l, i) => (
                <Nav key={i} link={l} index={index + 1} onLinkClick={onLinkClick} />
              ))}
            </div>
          </div>
        ) : (
          <div key={link.title}>
            <button
              onClick={() => setOpen(!open)}
              className="flex gap-3 rounded-xl text-zinc-400 items-center justify-between px-3 py-2.5 w-full hover:bg-zinc-800/80 hover:text-zinc-100 transition-all duration-200"
            >
              <div className="flex gap-3 font-medium text-sm items-center">
                <link.icon size={18} className="text-zinc-500 shrink-0" />
                {link.title}
              </div>
            </button>
            {open && (
              <div className="ml-4 mt-1 space-y-0.5">
                {link?.links?.map((l, i) => (
                  <Nav key={i} link={l} index={index + 1} onLinkClick={onLinkClick} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={link.link}
      key={link.title}
      onClick={onLinkClick}
      className={clsx(linkBaseClass, isActive ? linkActiveClass : linkInactiveClass)}
    >
      {link.icon && (
        <link.icon size={18} className={clsx("shrink-0", isActive ? iconActiveClass : iconInactiveClass)} />
      )}
      {link.title}
    </Link>
  );
};
